import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  InputLabel,
  Typography,
  Alert,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Checkbox,
  Card,
  CardContent,
} from '@mui/material';
import { AutoAwesome as MagicIcon } from '@mui/icons-material';
import GoogleMaps from '../../../../components/Location';
import SbTextField from '../../../../components/Textfield';
import RadioButton from '../../../../components/RadioButton';
import GetRatesContext from '../../../../context/GetRates';
import { GetRatesContextType } from '../../Type';
import { DropdownOptionType } from '../../../../components/Dropdown/Type';
import { SelectOptions } from '../../StyleComponent';
import { WEIGHT_UNITS } from '../../Constant';
import { validatePincode, checkWeightConsistency, AISavedLocation } from '../../services/aiService';
import SavedLocations from './SavedLocations';
import DashedBtn from '../../BoxWise/DashedBtn';
import updateBoxId from '@Services/updateBoxId';

const ShipmentTypeList = [
  { value: 'parcel', label: 'Parcel' },
  { value: 'document', label: 'Document' },
];

interface Design1FormProps {
  onOpenMagicTool: () => void;
  aiFilledFields: string[];
  isBoxDescBlocking: boolean;
  onValidationChange: (hasError: boolean) => void;
}

export default function Design1Form({
  onOpenMagicTool,
  aiFilledFields,
  isBoxDescBlocking,
  onValidationChange,
}: Design1FormProps) {
  const {
    hideBoxes,
    setHideBoxes,
    boxes,
    setBoxes,
    ratesInput,
    setRateInput,
    pickup,
    setPickup,
    destination,
    setDestination,
  } = React.useContext(GetRatesContext) as GetRatesContextType;

  // Local Form fields matching context values
  const [pickupPinCode, setPickupPinCode] = useState(pickup.pincode || '');
  const [destinationPinCode, setDestinationPinCode] = useState(destination.pincode || '');
  const [weight, setWeight] = useState(ratesInput.weight || 0);
  const [weightUnit, setWeightUnit] = useState(ratesInput.weightUnit || 'kg');
  const [shipmentType, setShipmentType] = useState(ratesInput.shipmentType || 'parcel');

  // AI advisory validation states
  const [pickupPincodeSuggestion, setPickupPincodeSuggestion] = useState<string | null>(null);
  const [destPincodeSuggestion, setDestPincodeSuggestion] = useState<string | null>(null);
  const [weightWarning, setWeightWarning] = useState<string | null>(null);
  const [isWeightConfirmed, setIsWeightConfirmed] = useState(false);

  // Field focus states
  const weightInputRef = React.useRef<HTMLInputElement>(null);

  // Synchronize context changes to local states
  useEffect(() => {
    setPickupPinCode(pickup.pincode || '');
    setDestinationPinCode(destination.pincode || '');
    setWeight(ratesInput.weight || 0);
    setWeightUnit(ratesInput.weightUnit || 'kg');
    setShipmentType(ratesInput.shipmentType || 'parcel');
  }, [pickup, destination, ratesInput]);

  // Synchronize local states to context
  useEffect(() => {
    setRateInput({
      shipmentType,
      pickup: { ...pickup, pincode: pickupPinCode },
      destination: { ...destination, pincode: destinationPinCode },
      pickupPinCode,
      destinationPinCode,
      weight,
      weightUnit,
    });
  }, [pickupPinCode, destinationPinCode, weight, weightUnit, shipmentType]);

  // Trigger Pincode Suggestion validations
  useEffect(() => {
    if (pickup.city && pickupPinCode) {
      const check = validatePincode(pickupPinCode, pickup.city);
      setPickupPincodeSuggestion(check.valid ? null : check.suggestion || null);
    } else {
      setPickupPincodeSuggestion(null);
    }
  }, [pickup.city, pickupPinCode]);

  useEffect(() => {
    if (destination.city && destinationPinCode) {
      const check = validatePincode(destinationPinCode, destination.city);
      setDestPincodeSuggestion(check.valid ? null : check.suggestion || null);
    } else {
      setDestPincodeSuggestion(null);
    }
  }, [destination.city, destinationPinCode]);

  // Trigger Weight Consistency validations
  useEffect(() => {
    if (weight > 0 && !isWeightConfirmed) {
      const descText = shipmentType === 'document' ? 'document' : boxes[0]?.description || 'General Goods';
      const check = checkWeightConsistency(Number(weight), shipmentType === 'document' ? 1 : boxes.length, descText);
      setWeightWarning(check.status === 'warning' ? check.message || null : null);
    } else {
      setWeightWarning(null);
    }
  }, [weight, boxes, shipmentType, isWeightConfirmed]);

  // Check overall missing fields and box description validation to block rate calculation
  const missingPickup = !pickup.city || !pickupPinCode;
  const missingDest = !destination.city || !destinationPinCode;
  const missingWeight = !weight || Number(weight) <= 0;

  // Box description validation check
  const missingBoxDescriptions =
    shipmentType === 'parcel' && !hideBoxes && boxes.some((b: any) => !b.description?.trim());

  const hasBlockingErrors =
    missingPickup || missingDest || missingWeight || (isBoxDescBlocking && missingBoxDescriptions);

  useEffect(() => {
    onValidationChange(hasBlockingErrors);
  }, [hasBlockingErrors, onValidationChange]);

  const handleSavedLocationSelect = (loc: AISavedLocation, target: 'pickup' | 'destination') => {
    const formatted = {
      city: loc.city,
      state: loc.state,
      country: loc.country,
      pincode: loc.pincode,
    };
    if (target === 'pickup') {
      setPickup(formatted);
      setPickupPinCode(loc.pincode);
    } else {
      setDestination(formatted);
      setDestinationPinCode(loc.pincode);
    }
  };

  const handleBoxDescChange = (index: number, val: string) => {
    const updated = [...boxes];
    updated[index] = { ...updated[index], description: val };
    setBoxes(updated);
  };

  const handleAddBox = () => {
    const boxesTemp = structuredClone(boxes);
    boxesTemp.push({
      id: boxesTemp.length.toString(),
      weight: 5,
      length: 30,
      width: 30,
      height: 20,
      boxCopyCount: 0,
      description: '',
    } as any);
    const arr = updateBoxId(
      boxesTemp,
      { length: 0, height: 0, width: 0, weight: 0, boxCopyCount: 0, id: '0' } as any,
      0,
    );
    setBoxes(arr);
  };

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'pickupPinCode') setPickupPinCode(value);
    else if (name === 'destinationPinCode') setDestinationPinCode(value);
    else if (name === 'weight') {
      setWeight(value);
      setIsWeightConfirmed(false); // Reset warning block on manual edit
    }
  };

  const isFieldAiFilled = (fieldName: string) => aiFilledFields.includes(fieldName);

  return (
    <Box sx={{ p: 1 }}>
      {/* Magic Tool Header action */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          p: 2,
          border: '1px solid #EAEAEA',
          borderRadius: '12px',
          backgroundColor: '#F8F9FB',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MagicIcon sx={{ color: 'primary.main', animation: 'pulse 2s infinite' }} />
          <Box>
            <Typography variant="subtitle2" fontWeight="bold">
              AI-Assisted Autofill
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Upload an invoice or type a shipment text query to fill the form in seconds.
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={onOpenMagicTool}
          startIcon={<MagicIcon />}
          sx={{ textTransform: 'none', borderRadius: '20px' }}
        >
          Open Magic Tool
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Shipper Column */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
            Shipper Location
          </Typography>

          <SavedLocations type="shipper" onSelect={loc => handleSavedLocationSelect(loc, 'pickup')} />

          <Box sx={{ mb: 3 }}>
            <InputLabel required sx={{ mb: 1, fontWeight: '500' }}>
              Pickup Location
            </InputLabel>
            <Box sx={{ position: 'relative' }}>
              <GoogleMaps
                onSelect={(event: any) => {
                  const comp = event.address_components;
                  setPickup({
                    city: comp.find((x: any) => x.types.includes('locality'))?.long_name || '',
                    state: comp.find((x: any) => x.types.includes('administrative_area_level_1'))?.long_name || '',
                    country: comp.find((x: any) => x.types.includes('country'))?.long_name || '',
                    pincode: comp.find((x: any) => x.types.includes('postal_code'))?.long_name || '',
                  });
                }}
                placeholder="Pick up Location"
                required
                value={Object.values(pickup).filter(Boolean).join(', ')}
              />
              {isFieldAiFilled('pickup') && (
                <Chip
                  label="AI Extracted"
                  size="small"
                  color="info"
                  sx={{ position: 'absolute', right: 8, top: 8, height: 20, fontSize: '9px', fontWeight: 'bold' }}
                />
              )}
            </Box>
            {missingPickup && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                Required for rate calculation
              </Typography>
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            <SbTextField
              label="Pickup Pincode"
              placeholder="Pick up Pincode"
              required
              value={pickupPinCode}
              onChange={handleFieldChange}
              name="pickupPinCode"
              sx={{ backgroundColor: isFieldAiFilled('pickupPinCode') ? '#F4F8FD' : 'transparent' }}
            />
            {pickupPincodeSuggestion && (
              <Alert
                severity="warning"
                sx={{ mt: 1, py: 0.5, borderRadius: '8px' }}
                action={
                  <Button
                    color="warning"
                    size="small"
                    onClick={() => {
                      setPickupPinCode(pickupPincodeSuggestion);
                      setPickupPincodeSuggestion(null);
                    }}
                  >
                    Accept
                  </Button>
                }
              >
                Pincode doesn't match {pickup.city} — did you mean {pickupPincodeSuggestion}?
              </Alert>
            )}
          </Box>
        </Grid>

        {/* Consignee Column */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
            Consignee Location
          </Typography>

          <SavedLocations type="consignee" onSelect={loc => handleSavedLocationSelect(loc, 'destination')} />

          <Box sx={{ mb: 3 }}>
            <InputLabel required sx={{ mb: 1, fontWeight: '500' }}>
              Destination Location
            </InputLabel>
            <Box sx={{ position: 'relative' }}>
              <GoogleMaps
                onSelect={(event: any) => {
                  const comp = event.address_components;
                  setDestination({
                    city: comp.find((x: any) => x.types.includes('locality'))?.long_name || '',
                    state: comp.find((x: any) => x.types.includes('administrative_area_level_1'))?.long_name || '',
                    country: comp.find((x: any) => x.types.includes('country'))?.long_name || '',
                    pincode: comp.find((x: any) => x.types.includes('postal_code'))?.long_name || '',
                  });
                }}
                placeholder="Destination Location"
                required
                value={Object.values(destination).filter(Boolean).join(', ')}
              />
              {isFieldAiFilled('destination') && (
                <Chip
                  label="AI Extracted"
                  size="small"
                  color="info"
                  sx={{ position: 'absolute', right: 8, top: 8, height: 20, fontSize: '9px', fontWeight: 'bold' }}
                />
              )}
            </Box>
            {missingDest && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                Required for rate calculation
              </Typography>
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            <SbTextField
              label="Destination Pincode"
              placeholder="Destination Pincode"
              required
              value={destinationPinCode}
              onChange={handleFieldChange}
              name="destinationPinCode"
              sx={{ backgroundColor: isFieldAiFilled('destinationPinCode') ? '#F4F8FD' : 'transparent' }}
            />
            {destPincodeSuggestion && (
              <Alert
                severity="warning"
                sx={{ mt: 1, py: 0.5, borderRadius: '8px' }}
                action={
                  <Button
                    color="warning"
                    size="small"
                    onClick={() => {
                      setDestinationPinCode(destPincodeSuggestion);
                      setDestPincodeSuggestion(null);
                    }}
                  >
                    Accept
                  </Button>
                }
              >
                Pincode doesn't match {destination.city} — did you mean {destPincodeSuggestion}?
              </Alert>
            )}
          </Box>
        </Grid>

        {/* Package & Weight Row */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, borderTop: '1px solid #EAEAEA', pt: 2 }}>
            Shipment Specs
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <RadioButton
                label="Shipment Type"
                required
                name="shipmentType"
                onChange={(e: any) => setShipmentType(e.target.value)}
                defaultValue={shipmentType}
                value={shipmentType}
                list={ShipmentTypeList}
                sx={{ display: 'flex', columnGap: 4 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                <SbTextField
                  inputRef={weightInputRef}
                  type="number"
                  placeholder="Weight"
                  label="Weight"
                  required
                  value={weight ? weight.toString() : ''}
                  onChange={handleFieldChange}
                  name="weight"
                  sx={{ backgroundColor: isFieldAiFilled('weight') ? '#F4F8FD' : 'transparent' }}
                  endAdornment={
                    <FormControl style={{ width: '50%' }}>
                      <SelectOptions
                        value={weightUnit}
                        name="weightUnit"
                        renderValue={value => <>{value}</>}
                        onChange={(e: any) => setWeightUnit(e.target.value)}
                      >
                        {WEIGHT_UNITS?.map(({ display, value }: DropdownOptionType) => (
                          <MenuItem key={value} value={value}>
                            {display}
                          </MenuItem>
                        ))}
                      </SelectOptions>
                    </FormControl>
                  }
                />
                {isFieldAiFilled('weight') && (
                  <Chip
                    label="AI Extracted"
                    size="small"
                    color="info"
                    sx={{
                      position: 'absolute',
                      right: '110px',
                      top: '10px',
                      height: 20,
                      fontSize: '9px',
                      fontWeight: 'bold',
                      zIndex: 10,
                    }}
                  />
                )}
              </Box>
              {missingWeight && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  Required for rate calculation
                </Typography>
              )}

              {weightWarning && (
                <Alert
                  severity="info"
                  sx={{ mt: 1, py: 0.5, borderRadius: '8px' }}
                  action={
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button
                        color="info"
                        size="small"
                        onClick={() => {
                          weightInputRef.current?.focus();
                        }}
                      >
                        Review
                      </Button>
                      <Button
                        color="success"
                        size="small"
                        onClick={() => {
                          setIsWeightConfirmed(true);
                          setWeightWarning(null);
                        }}
                      >
                        Confirm
                      </Button>
                    </Box>
                  }
                >
                  {weightWarning}
                </Alert>
              )}
            </Grid>
          </Grid>
        </Grid>

        {/* Box-wise details (Parcel only) */}
        {shipmentType === 'parcel' && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Checkbox checked={!hideBoxes} onChange={() => setHideBoxes(!hideBoxes)} style={{ paddingLeft: 0 }} />
              <Box>
                <InputLabel sx={{ fontWeight: '500' }}>Add Box-wise Details</InputLabel>
                <Typography variant="caption" color="text.secondary">
                  Required to calculate volumetric dimensions and product validations
                </Typography>
              </Box>
            </Box>

            {!hideBoxes && (
              <Box sx={{ mt: 3, borderTop: '1px solid #EAEAEA', pt: 3 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                  Box-wise Details Review
                </Typography>

                <Grid container spacing={3}>
                  {boxes.map((box: any, index: number) => {
                    const descMissing = !box.description?.trim();
                    return (
                      <Grid item xs={12} sm={6} md={4} key={box.id}>
                        <Card
                          variant="outlined"
                          sx={{
                            borderRadius: '12px',
                            border: descMissing ? '1px solid #FF8A80' : '1px solid #EAEAEA',
                            position: 'relative',
                          }}
                        >
                          <CardContent sx={{ p: 2.5 }}>
                            <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 1.5 }}>
                              Box {index + 1}
                            </Typography>
                            <Grid container spacing={1} sx={{ mb: 2 }}>
                              <Grid item xs={3}>
                                <Typography variant="caption" color="text.secondary">
                                  Weight
                                </Typography>
                                <Typography variant="body2" fontWeight="500">
                                  {box.weight} {weightUnit}
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography variant="caption" color="text.secondary">
                                  Length
                                </Typography>
                                <Typography variant="body2" fontWeight="500">
                                  {box.length} cm
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography variant="caption" color="text.secondary">
                                  Width
                                </Typography>
                                <Typography variant="body2" fontWeight="500">
                                  {box.width} cm
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <Typography variant="caption" color="text.secondary">
                                  Height
                                </Typography>
                                <Typography variant="body2" fontWeight="500">
                                  {box.height} cm
                                </Typography>
                              </Grid>
                            </Grid>

                            <Box>
                              <InputLabel required sx={{ mb: 0.5, fontSize: '12px' }}>
                                Product Description
                              </InputLabel>
                              <SbTextField
                                placeholder="E.g., Laptop, Clothes"
                                value={box.description || ''}
                                onChange={(e: any) => handleBoxDescChange(index, e.target.value)}
                                error={descMissing ? 'Description required' : ''}
                                sx={{ mb: 0 }}
                              />
                            </Box>

                            {descMissing && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5, display: 'block', fontSize: '10px' }}
                              >
                                {isBoxDescBlocking
                                  ? '[BLOCKING] Product description needed'
                                  : '[ADVISORY] Description missing'}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <DashedBtn label="Add a Box" onClick={handleAddBox} />
                </Box>
              </Box>
            )}
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
